
import React from "react";
import { Layout, Typography, Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { HomeOutlined, HeartOutlined } from "@ant-design/icons";
import { InlineShareButtons } from "sharethis-reactjs";

const { Title, Paragraph } = Typography;

// --- Styled Components ---
const UpdateContainer = styled.div`
  background: #000000;
  min-height: 100vh;
  padding: 40px 20px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UpdateContent = styled.div`
  max-width: 800px;
  width: 100%;
  background: #1a1a1a;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const UpdateTitle = styled(Title)`
  && {
    color: #ffcc00 !important;
    margin-bottom: 30px;
  }
`;

const UpdateParagraph = styled(Paragraph)`
  && {
    color: #fff !important;
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 30px;
  }
`;

const HighlightedText = styled.span`
  font-weight: bold;
  color: #ffaa00;
`;

const FeatureHighlight = styled.span`
  font-size: 24px;
  font-weight: bold;
  background-color: #0427f4;
  color: #ffffff;
  padding: 5px 10px;
  border-radius: 8px;
`;

const UpgradeHighlight = styled.span`
  font-size: 20px;
  font-weight: bold;
  background-color: #ffaa00;
  color: #000000;
  padding: 5px 10px;
  border-radius: 8px;
`;

const UpdateImage = styled.img`
  max-width: 100%;
  border-radius: 8px;
  margin: 20px 0;
  border: 2px solid #ffaa00;
`;

const BreadcrumbWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto 20px auto;
  text-align: left;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }
`;

const ShareThisWrapper = styled.div`
  margin-top: 30px;
  padding: 10px;
  background: #2a2a2a;
  border-radius: 8px;
`;

// --- Component Definition ---
const OLS2967: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Introducing the OLS Health Improvements Package",
    "description": "Discover the OLS Health Improvements Package, offering premium holistic wellness solutions for His Majesty King Charles III and all seeking vitality.",
    "url": "https://www.olsme.com/olsupdates/ols2900/2967",
    "datePublished": "2025-06-08",
    "author": {
      "@type": "Organization",
      "name": "OneLightSystem OLS",
      "url": "https://www.olsme.com",
    },
    "publisher": {
      "@type": "Organization",
      "name": "OneLightSystem OLS",
      "logo": "https://www.olsme.com/logo.jpg",
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://www.olsme.com/olsupdates/ols2900/2967",
    },
  };

  return (
    <Layout>
      <Helmet>
        <title>OLS Health Improvements Package: Premium Wellness Solutions</title>
        <meta
          name="description"
          content="Explore the OLS Health Improvements Package, offering premium holistic wellness solutions for His Majesty King Charles III and all seeking vitality."
        />
        <meta
          name="keywords"
          content="OLS Health, wellness package, holistic health, King Charles III, Sun Light Meditation, premium health solutions, OLS subscription"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="OneLightSystem OLS" />
        <link
          rel="canonical"
          href="https://www.olsme.com/olsupdates/ols2900/2967"
        />
        <meta property="og:title" content="OLS Health Improvements Package: Premium Wellness Solutions" />
        <meta property="og:description" content="Join OLS Health with our premium packages, crafted for His Majesty King Charles III and all seeking transformative wellness." />
        <meta property="og:url" content="https://www.olsme.com/olsupdates/ols2900/2967" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2Fols-health-package-announcement.png?alt=media&token=d2b4c6e8-1f9e-4b8a-9d8e-a0f3b2c4d5e6" />
        <meta property="og:image:alt" content="Announcement of OLS Health Improvements Package" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <UpdateContainer>
        <BreadcrumbWrapper>
          <Breadcrumb
            items={[
              { title: <Link to="/"><HomeOutlined /> Home</Link> },
              { title: <Link to="/olsupdates">OLS Updates</Link> },
              { title: "Health Package Announcement" },
            ]}
          />
        </BreadcrumbWrapper>
        <UpdateContent>
          <UpdateTitle level={1}><HeartOutlined style={{ marginRight: '15px' }} />Introducing the OLS Health Improvements Package</UpdateTitle>
          <UpdateParagraph>
            At <a href="https://www.olsme.com" style={{ color: "#ffaa00" }}>OneLightSystem (OLS)</a>, we are honored to extend a special invitation to <HighlightedText>His Majesty King Charles III</HighlightedText> and all who seek transformative wellness. As of <HighlightedText>June 8, 2025</HighlightedText>, we proudly announce the <FeatureHighlight>OLS Health Improvements Package</FeatureHighlight>, a premium suite of holistic health solutions designed to empower vitality and resilience.
          </UpdateParagraph>

          <UpdateImage
            src="https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2Fols-health-package-announcement.png?alt=media&token=d2b4c6e8-1f9e-4b8a-9d8e-a0f3b2c4d5e6"
            alt="A visual representation of the OLS Health Improvements Package, showcasing holistic wellness tools."
          />
          
          <UpdateParagraph>
            <strong>Why Choose OLS Health?</strong><br />
            Inspired by His Majesty’s resilience, our packages are crafted to support those who deserve the best in health and well-being. Key features include:<br />
            - <HighlightedText>Personalized Wellness Plans:</HighlightedText> Tailored programs integrating biofeedback and Sun Light Meditation techniques.<br />
            - <HighlightedText>Advanced Health Support:</HighlightedText> Access to cutting-edge tools for physical, mental, and emotional vitality.<br />
            - <HighlightedText>Expert Guidance:</HighlightedText> One-on-one consultations with OLS Health specialists.<br />
            - <HighlightedText>Community Access:</HighlightedText> Connect with a global network of wellness enthusiasts.<br />
            These premium packages are designed to deliver unparalleled care when conventional solutions fall short.
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>Our Premium Packages</strong><br />
            We offer three tiers to suit diverse needs, each requiring a subscription to unlock:<br />
            - <UpgradeHighlight>Basic Package:</UpgradeHighlight> Essential wellness tools and community access for foundational health support.<br />
            - <UpgradeHighlight>Advanced Package:</UpgradeHighlight> Enhanced biofeedback and personalized meditation plans for deeper transformation.<br />
            - <UpgradeHighlight>Elite Package:</UpgradeHighlight> Comprehensive care with priority consultations and exclusive health technologies.<br />
            While not free, these packages are an investment in lasting vitality, worthy of royalty and all who aspire to thrive.
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>A Special Offer to His Majesty</strong><br />
            In light of recent health reports, we humbly offer our OLS Health Improvements Package to His Majesty King Charles III, with tailored solutions to support his remarkable journey. Our team is ready to provide a confidential consultation at <a href="https://www.olsme.com/OLShealth" style={{ color: "#ffaa00" }}>olsme.com/OLShealth</a>.
          </UpdateParagraph>
          <UpdateParagraph style={{ fontWeight: 'bold', color: '#ffcc00' }}>
            Ready to embrace transformative wellness with OLS Health?
            <Link 
              to="/OLShealth/packages" 
              style={{ color: '#ffaa00', display: 'block', marginTop: '10px', textDecoration: 'underline' }}
            >
              Discover our packages at olsme.com/OLShealth/packages today!
            </Link>
          </UpdateParagraph>

          <ShareThisWrapper>
            <InlineShareButtons
              config={{
                alignment: "center",
                color: "social",
                enabled: true,
                networks: ["whatsapp", "linkedin", "messenger", "facebook", "twitter"],
                url: "https://www.olsme.com/olsupdates/ols2900/2967",
                image: "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2Fols-health-package-announcement.png?alt=media&token=d2b4c6e8-1f9e-4b8a-9d8e-a0f3b2c4d5e6",
                description: "OLS Health launches premium wellness packages for King Charles III and all seeking vitality. Join the revolution!",
                title: "OLS Health Improvements Package - OneLightSystem",
              }}
            />
          </ShareThisWrapper>
        </UpdateContent>
      </UpdateContainer>
    </Layout>
  );
};

export default OLS2967;